import React, { useState, useEffect, useRef } from "react";
import { Search, SlidersHorizontal, Calendar, ChevronDown, X } from "lucide-react";
import { LIST_STATUS_FILTERS } from "../../../constants/tspJourneyStatus";
import { DEFAULT_JOURNEY_FILTERS } from "../useTspJourneysListData";
import { formatDate, formatDateRange } from "../../../utils/tspFormat";

// Filter bar. The selects stay native <select>s (keyboard / screen-reader /
// mobile-picker friendly); the wrapper only supplies the design's thin chevron
// in place of the browser arrow. Changes are held as a draft and only sent to
// the list when Apply Filters is pressed (or Enter in the search box).
function FilterSelect({ label, value, onChange, children }) {
  return (
    <span className="tsp-filter-select-wrap">
      <select className="tsp-select tsp-filter-select" value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
        {children}
      </select>
      <ChevronDown size={16} aria-hidden="true" />
    </span>
  );
}

export default function JourneyFilters({ applied, options, onApply }) {
  const [draft, setDraft] = useState(applied);
  const [showDates, setShowDates] = useState(false);
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  const popoverRef = useRef(null);

  // Active filters count for phone filter badge
  const activeCount = [
    Boolean(draft.status && draft.status !== "all"),
    Boolean(draft.year),
    Boolean(draft.temple),
    Boolean(draft.stateId),
    Boolean(draft.from || draft.to),
  ].filter(Boolean).length;

  // Keep draft in sync when applied filters change (e.g., on reset or external navigation)
  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  // Click outside and Escape key handler for date popover
  useEffect(() => {
    if (!showDates) return;
    const handleOutsideClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowDates(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowDates(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDates]);

  const set = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }));

  // When year is explicitly selected, clear custom dates so they don't conflict
  const setYear = (val) => {
    setDraft((previous) => ({ ...previous, year: val, from: "", to: "" }));
  };

  const badRange = Boolean(draft.from && draft.to && draft.to < draft.from);
  const hasDates = Boolean(draft.from || draft.to);

  // Dynamic label for the Date Range button
  const getDateLabel = () => {
    if (draft.from && draft.to) {
      return formatDateRange(draft.from, draft.to);
    }
    if (draft.from) {
      return `From ${formatDate(draft.from)}`;
    }
    if (draft.to) {
      return `Until ${formatDate(draft.to)}`;
    }
    return "Date Range";
  };

  // Clear date range filter
  const clearDates = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const next = { ...draft, from: "", to: "" };
    setDraft(next);
    setShowDates(false);
    if (applied.from || applied.to) {
      onApply(next);
    }
  };

  // Apply date range directly from inside the popover
  const applyDateRange = (e) => {
    if (e) e.preventDefault();
    if (!badRange) {
      setShowDates(false);
      const next = { ...draft };
      // If custom date range crosses or doesn't match selected year, clear year so it filters cleanly
      if (next.from || next.to) {
        if (next.year) {
          const fromYear = next.from ? next.from.slice(0, 4) : null;
          const toYear = next.to ? next.to.slice(0, 4) : null;
          if ((fromYear && fromYear !== next.year) || (toYear && toYear !== next.year)) {
            next.year = "";
          }
        }
      }
      setDraft(next);
      onApply(next);
    }
  };

  // A year from the URL that no journey has (yet) stays selectable, so the select shows what is applied.
  const yearOptions = [...new Set([...(options.years || []), ...(draft.year ? [Number(draft.year)] : [])])].sort((a, b) => b - a);

  const submit = (event) => {
    if (event) event.preventDefault();
    if (!badRange) {
      setShowDates(false);
      setPhoneFiltersOpen(false);
      const next = { ...draft };
      if (next.from || next.to) {
        if (next.year) {
          const fromYear = next.from ? next.from.slice(0, 4) : null;
          const toYear = next.to ? next.to.slice(0, 4) : null;
          if ((fromYear && fromYear !== next.year) || (toYear && toYear !== next.year)) {
            next.year = "";
          }
        }
      }
      setDraft(next);
      onApply(next);
    }
  };

  const reset = () => {
    setDraft(DEFAULT_JOURNEY_FILTERS);
    setShowDates(false);
    setPhoneFiltersOpen(false);
    onApply(DEFAULT_JOURNEY_FILTERS);
  };

  return (
    <form className={`tsp-journey-filters ${phoneFiltersOpen ? "tsp-phone-filters-open" : ""}`} onSubmit={submit}>
      <div className="tsp-phone-filter-bar">
        <label className="tsp-journey-search">
          <Search size={16} />
          <input type="text" placeholder="Search journeys..." aria-label="Search journeys" value={draft.q} onChange={(event) => set("q")(event.target.value)} />
        </label>
        <button
          type="button"
          className={`tsp-phone-filter-toggle ${phoneFiltersOpen ? "is-open" : ""} ${activeCount > 0 ? "has-active" : ""}`}
          onClick={() => setPhoneFiltersOpen((open) => !open)}
          aria-expanded={phoneFiltersOpen}
          aria-label="Toggle filters"
        >
          <SlidersHorizontal size={15} />
          <span>Filters</span>
          {activeCount > 0 && <span className="tsp-phone-filter-badge">{activeCount}</span>}
        </button>
      </div>

      <div className="tsp-journey-filter-body">
        <div className="tsp-journey-filter-grid">
          <FilterSelect label="Journey status filter" value={draft.status} onChange={set("status")}>
            {Object.entries(LIST_STATUS_FILTERS).map(([key, filter]) => (
              <option key={key} value={key}>
                {filter.label}
              </option>
            ))}
          </FilterSelect>
          {/* Year-wise journeys: the years of real journeys (server-derived from start dates), newest first. */}
          <FilterSelect label="Year filter" value={draft.year || ""} onChange={setYear}>
            <option value="">All Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </FilterSelect>
          {options.temples && options.temples.length > 0 && (
            <FilterSelect label="Temple filter" value={draft.temple || ""} onChange={set("temple")}>
              <option value="">Temple</option>
              {options.temples.map((temple) => (
                <option key={temple} value={temple}>
                  {temple}
                </option>
              ))}
            </FilterSelect>
          )}
          <FilterSelect label="State filter" value={draft.stateId} onChange={set("stateId")}>
            <option value="">State</option>
            {options.states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </FilterSelect>
        </div>

        <div className="tsp-filter-date-wrap" ref={popoverRef}>
          <button
            type="button"
            className={`tsp-select tsp-date-range-btn ${hasDates ? "tsp-date-range-btn-active" : ""}`}
            aria-expanded={showDates}
            onClick={() => setShowDates((open) => !open)}
            title={hasDates ? `Date Range: ${getDateLabel()}` : "Filter by date range"}
          >
            <Calendar size={14} className="tsp-date-icon" />
            <span className="tsp-date-range-text">{getDateLabel()}</span>
            {hasDates ? (
              <span
                role="button"
                tabIndex={0}
                className="tsp-date-clear-icon"
                title="Clear date range"
                onClick={clearDates}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") clearDates(e);
                }}
              >
                <X size={13} />
              </span>
            ) : (
              <ChevronDown size={14} className="tsp-date-chevron" />
            )}
          </button>

          {showDates && (
            <div className="tsp-date-range-popover" role="dialog" aria-label="Select date range">
              <div className="tsp-date-range-popover-header">
                <span className="tsp-popover-title">Filter by Date Range</span>
                <button
                  type="button"
                  className="tsp-popover-close-btn"
                  onClick={() => setShowDates(false)}
                  aria-label="Close date range picker"
                >
                  <X size={15} />
                </button>
              </div>

              <div className="tsp-date-range-inputs">
                <div className="tsp-date-input-group">
                  <label htmlFor="tsp-date-from">From Date</label>
                  <input
                    id="tsp-date-from"
                    type="date"
                    className="tsp-input"
                    aria-label="From date"
                    value={draft.from || ""}
                    max={draft.to || undefined}
                    onChange={(event) => set("from")(event.target.value)}
                  />
                </div>
                <div className="tsp-date-input-group">
                  <label htmlFor="tsp-date-to">To Date</label>
                  <input
                    id="tsp-date-to"
                    type="date"
                    className="tsp-input"
                    aria-label="To date"
                    value={draft.to || ""}
                    min={draft.from || undefined}
                    onChange={(event) => set("to")(event.target.value)}
                  />
                </div>
              </div>

              {badRange && (
                <span className="tsp-form-error tsp-date-popover-error" role="alert">
                  The end date cannot be before the start date.
                </span>
              )}

              <div className="tsp-date-range-actions">
                <button
                  type="button"
                  className="tsp-btn tsp-btn-outline tsp-btn-sm"
                  onClick={clearDates}
                  disabled={!draft.from && !draft.to}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="tsp-btn tsp-btn-primary tsp-btn-sm"
                  onClick={applyDateRange}
                  disabled={badRange}
                >
                  Apply Range
                </button>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="tsp-btn tsp-btn-primary tsp-btn-lg tsp-apply-filters-btn" disabled={badRange}>
          <SlidersHorizontal size={14} /> Apply Filters
        </button>
        <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-lg tsp-filter-reset-btn" onClick={reset}>
          Reset
        </button>
      </div>
    </form>
  );
}
